import React from "react";

import { Badge } from "@/components/ui/Badge";

/**
 * Two sizes, same words and same colours.
 *
 * `md` is the table size: a fixed 80px pill, so a column of them lines up.
 * `sm` is for a line of meta text — a tenant header, a row in a picker — where
 * that fixed width dwarfs the 10-11px text beside it and the pill reads as the
 * loudest thing on the row. `sm` shrinks to its label and sits at the height of
 * the line it is on.
 */
const SIZES = {
    md: {
        wrapper: "min-h-6 min-w-20 border-[1px] text-center rounded-[2rem]",
        inner: "text-[11px]",
    },
    sm: {
        wrapper: "min-h-0 min-w-0 border-[1px] rounded-full",
        inner: "px-2 py-0.5 text-[10px] font-medium leading-[1.4]",
    },
};

/**
 * Status badge with three states:
 *  - Deleted  → slate (colorIndex 8) — takes precedence over isActive
 *  - Active   → emerald (colorIndex 3)
 *  - Inactive → rose (colorIndex 6)
 *
 * Callers can still pass `text` + `colorIndex` to force a custom render
 * (used by a few legacy spots like the user list "Pending" tag).
 *
 * @param {"md"|"sm"} [size] see SIZES above. Defaults to the table size.
 */
const StatusBadge = ({ isActive, isDeleted = false, text = '', colorIndex, size = "md" }) => {
    // Normalize incoming value to boolean
    const active = Boolean(isActive);
    const deleted = Boolean(isDeleted);

    const defaultLabel = deleted ? "Deleted" : active ? "Active" : "Inactive";
    const defaultColor = deleted ? 8 : active ? 3 : 6;
    const { wrapper, inner } = SIZES[size] ?? SIZES.md;

    return (
        <Badge
            label={text ? text : defaultLabel}
            badgeStyle={inner}
            variant="gradient"
            colorIndex={colorIndex != null ? colorIndex : defaultColor}
            className={wrapper}
        />
    );
};

export default StatusBadge;